import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

/* ─── Types ──────────────────────────────────────────────────── */
interface Props {
  tenantId:      string;
  assistantName: string;
  primary:       string;
}

type View = 'closed' | 'greeting' | 'form' | 'success';

type FormState = {
  name:       string;
  phone:      string;
  email:      string;
  event_type: string;
  event_date: string;
  message:    string;
};

const EMPTY_FORM: FormState = {
  name: '', phone: '', email: '', event_type: '', event_date: '', message: '',
};

const QUICK_REPLIES = [
  { id: 'pricing',      label: 'I want pricing',     icon: 'ri-price-tag-3-line',    msg: "Hi! I'd like to know more about your pricing and packages." },
  { id: 'availability', label: 'I need availability', icon: 'ri-calendar-check-line', msg: "I'd like to check your availability for my event." },
  { id: 'book',         label: 'I want to book',      icon: 'ri-bookmark-line',       msg: "I'm ready to book a session — please guide me through the next steps." },
  { id: 'other',        label: 'Something else',      icon: 'ri-chat-1-line',         msg: '' },
];

const EVENT_TYPES = [
  'Wedding', 'Engagement Session', 'Portrait Session',
  'Family Photos', 'Commercial Shoot', 'Corporate Event', 'Other',
];

const INPUT =
  'w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-[#1A1A2E] placeholder-slate-300 outline-none transition-colors focus:border-[#C8A96E]/50 focus:bg-white';

/* ─── Typing dots ─────────────────────────────────────────────── */
function TypingDots({ color }: { color: string }) {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full animate-bounce"
          style={{ backgroundColor: color, animationDelay: `${i * 0.15}s`, opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

/* ─── Widget ──────────────────────────────────────────────────── */
export default function AiChatWidget({ tenantId, assistantName, primary }: Props) {
  const [view, setView]                 = useState<View>('closed');
  const [form, setForm]                 = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors]             = useState<Partial<FormState>>({});
  const [submitting, setSubmitting]     = useState(false);
  const [serverError, setServerError]   = useState('');
  const [showTyping, setShowTyping]     = useState(false);
  const [botMessages, setBotMessages]   = useState<string[]>([]);
  const [selectedReply, setSelectedReply] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  /* ── Scroll to bottom ── */
  useEffect(() => {
    if (view !== 'closed') {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [botMessages, view, showTyping]);

  /* ── Save a message to DB (fire-and-forget) ── */
  const saveMessage = useCallback(
    (convId: string, role: string, content: string) => {
      supabase.from('ai_messages').insert({
        tenant_id:       tenantId,
        conversation_id: convId,
        role,
        content,
      }).then(() => {});
    },
    [tenantId],
  );

  /* ── Create conversation record ── */
  const createConversation = useCallback(async (): Promise<string | null> => {
    const { data } = await supabase
      .from('ai_conversations')
      .insert({
        tenant_id: tenantId,
        channel:   'website',
        status:    'open',
      })
      .select('id')
      .maybeSingle();
    return data?.id ?? null;
  }, [tenantId]);

  /* ── Open widget ── */
  const openWidget = async () => {
    setView('greeting');
    setShowTyping(true);
    setBotMessages([]);
    setSelectedReply(null);

    // Create conversation in background
    const convId = await createConversation();
    setConversationId(convId);

    const greetings = [
      `Hi there! I'm ${assistantName} 👋`,
      "How can I help you today? Choose an option or tell me what you're looking for.",
    ];

    setTimeout(() => {
      setShowTyping(false);
      setBotMessages(greetings);
      if (convId) {
        greetings.forEach((g) => saveMessage(convId, 'assistant', g));
      }
    }, 1200);
  };

  const closeWidget = () => {
    setView('closed');
    setForm(EMPTY_FORM);
    setErrors({});
    setServerError('');
    setSelectedReply(null);
    setBotMessages([]);
    // keep conversationId — conversation stays open unless closed by admin
  };

  /* ── Quick reply ── */
  const handleQuickReply = (reply: typeof QUICK_REPLIES[0]) => {
    setSelectedReply(reply.id);

    // Save user message
    if (conversationId && reply.msg) {
      saveMessage(conversationId, 'user', reply.label);
    }

    setShowTyping(true);

    const botReply =
      reply.id === 'other'
        ? "Of course! Please leave your details below and we'll reach out to you."
        : "Great choice! To give you the best answer, I'll need a few quick details. Please fill out this form:";

    setTimeout(() => {
      setShowTyping(false);
      setBotMessages((prev) => [...prev, botReply]);
      if (conversationId) saveMessage(conversationId, 'assistant', botReply);
      setForm((prev) => ({ ...prev, message: reply.msg }));
      setTimeout(() => setView('form'), 400);
    }, 900);
  };

  /* ── Validation ── */
  const validate = (): boolean => {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() && !form.phone.trim())
      e.email = 'Email or phone required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Enter a valid email';
    if (form.message.length > 500) e.message = 'Max 500 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError('');

    // Save lead
    const { data: lead, error } = await supabase
      .from('tenant_public_leads')
      .insert({
        tenant_id:  tenantId,
        name:       form.name.trim(),
        email:      form.email.trim() || null,
        phone:      form.phone.trim() || null,
        event_type: form.event_type   || null,
        event_date: form.event_date   || null,
        message:    form.message.trim() || null,
        status:     'new',
        source:     'ai_assistant',
      })
      .select('id')
      .maybeSingle();

    if (error) {
      setServerError('Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }

    // Update conversation with visitor info + lead_id
    if (conversationId) {
      await supabase
        .from('ai_conversations')
        .update({
          visitor_name:  form.name.trim() || null,
          visitor_email: form.email.trim() || null,
          visitor_phone: form.phone.trim() || null,
          event_type:    form.event_type   || null,
          event_date:    form.event_date   || null,
          lead_id:       lead?.id ?? null,
          updated_at:    new Date().toISOString(),
        })
        .eq('id', conversationId);

      // Save user message with form summary
      const summary = [
        form.message.trim(),
        form.name ? `Name: ${form.name}` : '',
        form.email ? `Email: ${form.email}` : '',
        form.phone ? `Phone: ${form.phone}` : '',
        form.event_type ? `Event: ${form.event_type}` : '',
        form.event_date ? `Date: ${form.event_date}` : '',
      ]
        .filter(Boolean)
        .join(' | ');

      saveMessage(conversationId, 'user', summary);

      // Bot confirmation
      saveMessage(
        conversationId,
        'assistant',
        `Thank you, ${form.name}! We received your request and will be in touch soon.`,
      );
    }

    setSubmitting(false);
    setView('success');
  };

  const set = (key: keyof FormState, val: string) =>
    setForm((p) => ({ ...p, [key]: val }));

  /* ── Render ── */
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

      {/* ── Panel ── */}
      {view !== 'closed' && (
        <div
          className="w-80 bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col"
          style={{ maxHeight: '560px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2.5 shrink-0" style={{ backgroundColor: primary }}>
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 shrink-0">
              <i className="ri-robot-line text-white text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{assistantName}</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                <span className="text-white/70 text-[10px]">Online now</span>
              </div>
            </div>
            <button
              type="button"
              onClick={closeWidget}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-white text-sm" />
            </button>
          </div>

          {/* Chat body */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">

            {botMessages.map((msg, i) => (
              <div key={i} className="flex items-end gap-1.5">
                <div
                  className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                  style={{ backgroundColor: primary + '25' }}
                >
                  <i className="ri-robot-line text-[10px]" style={{ color: primary }} />
                </div>
                <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 max-w-[80%] border border-slate-200">
                  <p className="text-[#1A1A2E] text-[11px] leading-relaxed">{msg}</p>
                </div>
              </div>
            ))}

            {showTyping && (
              <div className="flex items-end gap-1.5">
                <div
                  className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                  style={{ backgroundColor: primary + '25' }}
                >
                  <i className="ri-robot-line text-[10px]" style={{ color: primary }} />
                </div>
                <div className="bg-white rounded-2xl rounded-bl-sm border border-slate-200">
                  <TypingDots color={primary} />
                </div>
              </div>
            )}

            {/* Quick replies */}
            {view === 'greeting' && !showTyping && botMessages.length >= 2 && (
              <div className="space-y-2 pt-1">
                {QUICK_REPLIES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => handleQuickReply(r)}
                    disabled={selectedReply !== null}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all cursor-pointer text-xs font-medium ${
                      selectedReply === r.id
                        ? 'border-[#C8A96E]/50 bg-[#C8A96E]/10 text-[#1A1A2E]'
                        : selectedReply !== null
                        ? 'border-slate-200 bg-white text-slate-400 opacity-50 cursor-not-allowed'
                        : 'border-slate-200 bg-white text-[#1A1A2E] hover:border-[#C8A96E]/40 hover:bg-[#C8A96E]/5'
                    }`}
                  >
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0"
                      style={{ backgroundColor: selectedReply === r.id ? primary + '20' : '#f1f5f9' }}
                    >
                      <i
                        className={`${r.icon} text-xs`}
                        style={{ color: selectedReply === r.id ? primary : '#94a3b8' }}
                      />
                    </div>
                    {r.label}
                    {selectedReply === r.id && (
                      <i className="ri-check-line ml-auto text-xs" style={{ color: primary }} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Lead form */}
            {view === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-2.5 pt-1">
                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">
                    Your Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text" value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="Jane Doe" maxLength={80}
                    className={`${INPUT} ${errors.name ? 'border-red-300' : ''}`}
                  />
                  {errors.name && <p className="text-red-400 text-[10px] mt-0.5">{errors.name}</p>}
                </div>

                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">Phone</label>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="+1 555 000 0000"
                    className={INPUT}
                  />
                </div>

                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">Email</label>
                  <input
                    type="email" value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="jane@email.com"
                    className={`${INPUT} ${errors.email ? 'border-red-300' : ''}`}
                  />
                  {errors.email && <p className="text-red-400 text-[10px] mt-0.5">{errors.email}</p>}
                </div>

                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">Event Type</label>
                  <div className="relative">
                    <select
                      value={form.event_type}
                      onChange={(e) => set('event_type', e.target.value)}
                      className={`${INPUT} cursor-pointer appearance-none`}
                    >
                      <option value="">Select…</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs" />
                  </div>
                </div>

                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">Event Date</label>
                  <input
                    type="date" value={form.event_date}
                    onChange={(e) => set('event_date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={INPUT}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-slate-400 text-[10px]">Message</label>
                    <span className={`text-[10px] ${form.message.length > 450 ? 'text-amber-500' : 'text-slate-300'}`}>
                      {form.message.length}/500
                    </span>
                  </div>
                  <textarea
                    rows={3} value={form.message}
                    onChange={(e) => set('message', e.target.value)}
                    maxLength={500}
                    placeholder="Tell us about your event, vision, location…"
                    className={`${INPUT} resize-none ${errors.message ? 'border-red-300' : ''}`}
                  />
                  {errors.message && <p className="text-red-400 text-[10px] mt-0.5">{errors.message}</p>}
                </div>

                {serverError && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
                    <i className="ri-error-warning-line text-red-400 text-xs shrink-0" />
                    <p className="text-red-500 text-[10px]">{serverError}</p>
                  </div>
                )}

                <button
                  type="submit" disabled={submitting}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  style={{ backgroundColor: primary }}
                >
                  {submitting ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
                  ) : (
                    <><i className="ri-send-plane-line text-xs" /> Send Request</>
                  )}
                </button>
              </form>
            )}

            {/* Success */}
            {view === 'success' && (
              <div className="flex flex-col items-center text-center py-4 gap-3">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: primary + '20' }}
                >
                  <i className="ri-check-double-line text-xl" style={{ color: primary }} />
                </div>
                <div>
                  <p className="text-[#1A1A2E] font-semibold text-xs">Message sent! 🎉</p>
                  <p className="text-slate-400 text-[11px] mt-1 leading-relaxed">
                    We got your details and will reach out as soon as possible.
                  </p>
                </div>
                <button
                  type="button" onClick={closeWidget}
                  className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  Close chat
                </button>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {view !== 'success' && (
            <div className="px-3 py-2 border-t border-slate-100 bg-white shrink-0">
              <p className="text-slate-300 text-[9px] text-center">
                Powered by{' '}
                <span className="font-medium" style={{ color: primary }}>AI Assistant</span>
                {' '}— responses may take up to 24h
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Floating button ── */}
      <button
        type="button"
        onClick={view === 'closed' ? openWidget : closeWidget}
        className="flex items-center gap-2 px-4 py-3 rounded-full font-semibold text-sm text-white transition-all cursor-pointer whitespace-nowrap"
        style={{ backgroundColor: primary, boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}
      >
        <i className={view === 'closed' ? 'ri-robot-line' : 'ri-close-line'} />
        {view === 'closed' ? 'Chat with us' : 'Close'}
      </button>
    </div>
  );
}